import { create } from "zustand";

// Interfaz para un evento del socket
export interface SocketEvent {
  id: string;
  device_id: string;
  company_id: string | null;
  vehicle_id: string | null;
  remolque_id: string | null;
  type: string | null;
  event_type: string | null;
  event_code: number | null;
  lat: number | null;
  lon: number | null;
  timeStamp: number | null;
  gps_sat: number | null;
  engine_status: number | null;
  odo_km: number | null;
  rssi: number | null;
  sim: string | null;
  /**
   * Parámetros crudos del evento tal como los emitió el parser.
   *
   * Se conservan enteros —no campo por campo— porque cada familia de equipo
   * manda los suyos. Para dashcam vienen `dvr_event_file` (el nombre del video
   * que grabó), `dvr_camera` (front | inward) y `dvr_event_kind`.
   */
  params: Record<string, unknown> | null;
  receivedAt: number; // Timestamp de cuando se recibió el evento
}

/** El archivo de video que la cámara grabó para este evento, si lo hay. */
export const dashcamEventFile = (event: SocketEvent): string | null => {
  const file = event.params?.dvr_event_file;
  return typeof file === "string" && file ? file : null;
};

/** Qué cámara lo grabó: `front` (carretera) o `inward` (cabina). */
export const dashcamEventCamera = (event: SocketEvent): string | null => {
  const camera = event.params?.dvr_camera;
  return typeof camera === "string" && camera ? camera : null;
};

export const EVENT_CODE_KEYS: Record<string, string> = {
  "1": "speed_alert",
  "2": "unauthorized_movement",
  "3": "engine_on",
  "4": "engine_off",
  "5": "panic",
  "6": "position",
  "7": "hook",
  "8": "release",
  "9": "low_battery",
  "10": "init_charge",
  "11": "end_charge",
  "12": "poleo",
  "13": "call_poleo",
  "14": "door_open",
  "15": "door_close",
  "16": "engine_stop_active",
  "17": "engine_stop_deactivated",
  "18": "power_disconnection",
  "19": "power_connection",
  "20": "temperature",
  "21": "gas",
  "22": "device_off",
  "23": "zone_in",
  "24": "zone_out",
  "25": "turn",
  "26": "on_motion",
  "27": "off_motion",
  "28": "on_motion_zone",
  "29": "backup_energy_position",
  "30": "periodic_reset",
  "31": "un_hook",
  "32": "magnetic_look_open",
  "33": "magnetic_look_close",
  "34": "acceleration",
  "35": "braking",
  "36": "jamming_detection",
  "37": "towing",
  "38": "reverse",
  "40": "refuelling",
  "41": "fuel_extraction",
  "42": "reverse_inactive",
  "43": "other",
  "44": "pause",
  "45": "alert",
  "46": "return_pause",
  "47": "return_alert",

  // Reporte periódico sin fix de GPS: el equipo está vivo pero no sabe dónde
  // está (la dashcam apaga el GPS con la ignición apagada). Llega sin lat/lon,
  // por eso el tipo de `lat` ya es nullable.
  "48": "position_no_gps",

  // Dashcam, bloque 60-72. Arranca en 60 y no en 43 porque el 43-47 de arriba
  // ya estaba tomado; el hueco entre 47 y 60 queda libre para que esos crezcan.
  "60": "dms_eyes_closed",
  "61": "dms_yawn",
  "62": "dms_distraction",
  "63": "dms_smoking",
  "64": "dms_phone",
  "65": "dms_no_face",
  "66": "dms_fatigue",
  "67": "adas_forward_collision",
  "68": "adas_headway",
  "69": "adas_lane_departure",
  // «Pasó algo y hay video», sin tipo todavía: la cámara no dice cuál fue.
  "72": "dvr_event"
};

/** Códigos que produce una dashcam. Sirve para filtrar su feed. */
export const DASHCAM_EVENT_CODES = [60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 72];

export const isDashcamEventCode = (code?: number | null): boolean =>
  typeof code === "number" && DASHCAM_EVENT_CODES.includes(code);

// Traducciones en español para los eventos
export const EVENT_CODE_LABELS_ES: Record<string, string> = {
  "1": "Alerta de velocidad",
  "2": "Movimiento no autorizado",
  "3": "Motor encendido",
  "4": "Motor apagado",
  "5": "Pánico",
  "6": "Posición",
  "7": "Enganche",
  "8": "Desenganche",
  "9": "Batería baja",
  "10": "Inicio de carga",
  "11": "Fin de carga",
  "12": "Poleo",
  "13": "Llamada de poleo",
  "14": "Puerta abierta",
  "15": "Puerta cerrada",
  "16": "Bloqueo de motor activo",
  "17": "Bloqueo de motor desactivado",
  "18": "Desconexión de energía",
  "19": "Conexión de energía",
  "20": "Temperatura",
  "21": "Gas",
  "22": "Dispositivo apagado",
  "23": "Entrada a zona",
  "24": "Salida de zona",
  "25": "Giro",
  "26": "Movimiento activado",
  "27": "Movimiento desactivado",
  "28": "Movimiento en zona",
  "29": "Posición con energía de respaldo",
  "30": "Reinicio periódico",
  "31": "Desenganche",
  "32": "Cerradura magnética abierta",
  "33": "Cerradura magnética cerrada",
  "34": "Aceleración",
  "35": "Frenado",
  "36": "Detección de interferencia",
  "37": "Remolque",
  "38": "Reversa",
  "40": "Recarga de combustible",
  "41": "Extracción de combustible",
  "42": "Reversa inactiva",
  "43": "Otro",
  "44": "Pausa",
  "45": "Alerta",
  "46": "Retorno de pausa",
  "47": "Retorno de alerta",
  "48": "Reporte sin GPS",
  "60": "Ojos cerrados",
  "61": "Bostezo",
  "62": "Distracción",
  "63": "Fumando",
  "64": "Uso de celular",
  "65": "Sin rostro detectado",
  "66": "Fatiga del conductor",
  "67": "Colisión frontal inminente",
  "68": "Vehículo demasiado cerca",
  "69": "Salida de carril",
  "72": "Evento de cámara"
};

// Función para obtener la etiqueta del evento en español
export const getEventLabel = (eventCode: number | null): string => {
  if (eventCode === null) return "Evento desconocido";
  const label = EVENT_CODE_LABELS_ES[String(eventCode)];
  if (!label) return `Evento #${eventCode}`;
  return label;
};

interface DeviceEventsStore {
  // Almacena eventos por device_id (máximo 50 eventos por device)
  eventsByDevice: Record<string, SocketEvent[]>;

  // Máximo de eventos a almacenar por device
  maxEventsPerDevice: number;

  // Agregar un evento
  addEvent: (eventData: Omit<SocketEvent, "id" | "receivedAt">) => void;

  // Obtener eventos de un device
  getDeviceEvents: (deviceId: string) => SocketEvent[];

  // Limpiar eventos de un device
  clearDeviceEvents: (deviceId: string) => void;

  // Limpiar todos los eventos
  clearAllEvents: () => void;
}

export const useDeviceEventsStore = create<DeviceEventsStore>((set, get) => ({
  eventsByDevice: {},
  maxEventsPerDevice: 50,

  addEvent: (eventData) => {
    const deviceId = eventData.device_id;
    if (!deviceId) return;

    const newEvent: SocketEvent = {
      ...eventData,
      id: `${deviceId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      receivedAt: Date.now(),
    };

    set((state) => {
      const currentEvents = state.eventsByDevice[deviceId] || [];

      const updatedEvents = [newEvent, ...currentEvents].slice(
        0,
        state.maxEventsPerDevice
      );

      const newEventsByDevice = {
        ...state.eventsByDevice,
        [deviceId]: updatedEvents,
      };

      // Limitar a 100 devices con eventos para evitar acumulación infinita
      const deviceKeys = Object.keys(newEventsByDevice);
      if (deviceKeys.length > 100) {
        const sortedKeys = deviceKeys.sort((a, b) => {
          const aLast = newEventsByDevice[a]?.[0]?.receivedAt ?? 0;
          const bLast = newEventsByDevice[b]?.[0]?.receivedAt ?? 0;
          return bLast - aLast;
        });
        const keysToRemove = sortedKeys.slice(100);
        for (const key of keysToRemove) {
          delete newEventsByDevice[key];
        }
      }

      return { eventsByDevice: newEventsByDevice };
    });
  },

  getDeviceEvents: (deviceId) => {
    return get().eventsByDevice[deviceId] || [];
  },

  clearDeviceEvents: (deviceId) => {
    set((state) => {
      const { [deviceId]: _, ...rest } = state.eventsByDevice;
      return { eventsByDevice: rest };
    });
  },

  clearAllEvents: () => {
    set({ eventsByDevice: {} });
  },
}));
